// src/pages/Suppliers.tsx — Sprint 2 G09: ABN verify · Sprint 3 G10: compliance badges
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_SUPPLIERS, SUPPLIER_CATEGORY_META, suggestSuppliers, Supplier, SupplierCategory, SupplierStatus } from '../utils/mockDataSuppliers'
import { MOCK_JOBS } from '../utils/mockData'
import { getComplianceForSupplier, getSupplierComplianceStatus, COMPLIANCE_STATUS_META, DOC_TYPE_META } from '../utils/supplierCompliance'
import { Search, Plus, Star, MapPin, Phone, Mail, Globe, CheckCircle, AlertTriangle, Clock, Building, ChevronRight, X, Loader, Shield } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { fetchSuppliers, createSupplier } from '../services/suppliersService'

function StarRating({ rating }: { rating: number }) {
  return <div className="flex items-center gap-0.5">{[1,2,3,4,5].map(i=><Star key={i} className={`w-3 h-3 ${i<=rating?'text-amber-400 fill-amber-400':'text-gray-200'}`}/>)}</div>
}

const STATUS_COLORS: Record<string,{bg:string;text:string}> = {
  ACTIVE:   {bg:'bg-green-100',text:'text-green-700'}, INACTIVE:{bg:'bg-gray-100',text:'text-gray-500'}, SUSPENDED:{bg:'bg-red-100',text:'text-red-700'} as any, PENDING_APPROVAL:{bg:'bg-amber-100',text:'text-amber-700'},
}

// G10 — Compliance badge for supplier card
function ComplianceBadge({ supplierId }: { supplierId: string }) {
  const status = getSupplierComplianceStatus(supplierId)
  const m = COMPLIANCE_STATUS_META[status]
  if (status === 'VALID') return null
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${m.bg} ${m.text} ${m.border}`}>{m.label}</span>
}

// G09 — ABN lookup simulation
type ABNStatus = 'IDLE' | 'CHECKING' | 'ACTIVE' | 'CANCELLED' | 'INVALID'
function useABNLookup() {
  const [status, setStatus] = useState<ABNStatus>('IDLE')
  const [entityName, setEntityName] = useState('')
  const lookup = async (abn: string) => {
    const digits = abn.replace(/\s/g,'')
    if (digits.length !== 11) { setStatus('INVALID'); return }
    setStatus('CHECKING')
    await new Promise(r=>setTimeout(r,800+Math.random()*500))
    if (digits.startsWith('00')) { setStatus('CANCELLED'); setEntityName('') }
    else { setStatus('ACTIVE'); setEntityName(`${abn.slice(0,2)} *** *** ***`) }
  }
  const reset = () => { setStatus('IDLE'); setEntityName('') }
  return { status, entityName, lookup, reset }
}

function ABNField({ abn, onChange }: { abn: string; onChange: (v:string)=>void }) {
  const { status, entityName, lookup, reset } = useABNLookup()
  const badgeClass = status==='ACTIVE'?'bg-green-100 text-green-700':status==='CANCELLED'?'bg-red-100 text-red-700':status==='INVALID'?'bg-red-100 text-red-700':'bg-gray-100 text-gray-500'
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">ABN</label>
      <div className="flex gap-2">
        <input className="input text-sm flex-1" value={abn} placeholder="00 000 000 000" onChange={e=>{onChange(e.target.value);reset()}}/>
        <button type="button" onClick={()=>lookup(abn)} disabled={status==='CHECKING'||!abn.replace(/\s/g,'').length} className="text-xs px-3 py-1.5 rounded-lg bg-teal-100 text-teal-700 hover:bg-teal-200 font-medium disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0">
          {status==='CHECKING'?<><Loader className="w-3 h-3 animate-spin"/>Checking…</>:'Verify ABN'}
        </button>
      </div>
      {status!=='IDLE'&&status!=='CHECKING'&&(
        <div className={`mt-1.5 text-xs px-2 py-1 rounded-lg font-medium inline-flex items-center gap-1 ${badgeClass}`}>
          {status==='ACTIVE'&&<><CheckCircle className="w-3 h-3"/>ACTIVE — {entityName}</>}
          {status==='CANCELLED'&&<><AlertTriangle className="w-3 h-3"/>CANCELLED — not a valid active ABN</>}
          {status==='INVALID'&&<><AlertTriangle className="w-3 h-3"/>INVALID — must be 11 digits</>}
        </div>
      )}
    </div>
  )
}

// G10 — Compliance detail panel section
function ComplianceSection({ supplierId }: { supplierId: string }) {
  const records = getComplianceForSupplier(supplierId)
  const overall = getSupplierComplianceStatus(supplierId)
  const om = COMPLIANCE_STATUS_META[overall]
  if (records.length === 0) return null
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Shield className="w-3.5 h-3.5 text-gray-500"/>
        <span className="text-xs font-semibold text-gray-700">Compliance</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${om.bg} ${om.text} ${om.border}`}>{om.label}</span>
      </div>
      <div className="space-y-1">
        {records.map(r=>{
          const dm = DOC_TYPE_META[r.docType]; const sm = COMPLIANCE_STATUS_META[r.status]
          return (
            <div key={r.docType} className={`flex items-center justify-between text-xs px-3 py-1.5 rounded-lg border ${sm.bg} ${sm.border}`}>
              <span className={`font-medium ${sm.text}`}>{dm.short}</span>
              <div className="text-right">
                {r.expiryDate?<span className={sm.text}>{r.daysUntilExpiry!=null&&r.daysUntilExpiry>=0?`${r.daysUntilExpiry}d remaining`:'Expired'}</span>:<span className="text-gray-400">Not provided</span>}
                {r.docReference&&<div className="text-[10px] text-gray-400 font-mono">{r.docReference}</div>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SupplierDetailPanel({ supplier, onClose }: { supplier: Supplier; onClose: ()=>void }) {
  const sc = STATUS_COLORS[supplier.status]
  const catMeta = SUPPLIER_CATEGORY_META[supplier.categories[0] as SupplierCategory]
  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.bg} ${sc.text}`}>{supplier.status}</span>
              {supplier.isPreferred&&<span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">⭐ Preferred</span>}
              <ComplianceBadge supplierId={supplier.id}/>
            </div>
            <div className="font-semibold text-gray-900">{supplier.companyName}</div>
            <div className="text-xs text-gray-500 mt-0.5">ABN {supplier.abn} · {supplier.categories.map(c=>SUPPLIER_CATEGORY_META[c as SupplierCategory]?.label??c).join(', ')}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400"/></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          {supplier.contactName&&<div><div className="text-xs text-gray-500 mb-0.5">Contact</div><div className="font-medium">{supplier.contactName}</div></div>}
          {supplier.phone&&<div><div className="text-xs text-gray-500 mb-0.5">Phone</div><div className="font-medium">{supplier.phone}</div></div>}
          {supplier.email&&<div className="col-span-2"><div className="text-xs text-gray-500 mb-0.5">Email</div><a href={`mailto:${supplier.email}`} className="text-[#2E75B6] font-medium">{supplier.email}</a></div>}
          {supplier.address&&<div className="col-span-2"><div className="text-xs text-gray-500 mb-0.5">Address</div><div className="font-medium">{supplier.address}</div></div>}
          {supplier.paymentTermsDays&&<div><div className="text-xs text-gray-500 mb-0.5">Payment terms</div><div className="font-medium">{supplier.paymentTermsDays} days</div></div>}
          <div><div className="text-xs text-gray-500 mb-0.5">Rating</div><StarRating rating={supplier.rating}/></div>
        </div>
        <ComplianceSection supplierId={supplier.id}/>
      </div>
    </div>
  )
}

export function Suppliers() {
  const { isTier } = useAuthStore()
  const canEdit = isTier('B_PC') || isTier('B_PM') || isTier('A_DEVELOPER')
  // Live suppliers from Neon — merged with mock fallback (de-dupe by ABN)
  const { data: apiSuppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['suppliers'],
    queryFn:  fetchSuppliers,
    staleTime: 60_000,
  })
  const apiAbns = new Set((apiSuppliers as Supplier[]).map((s: Supplier) => s.abn).filter(Boolean))
  const mergedSuppliers: Supplier[] = [
    ...(apiSuppliers as Supplier[]),
    ...MOCK_SUPPLIERS.filter((s: Supplier) => !s.abn || !apiAbns.has(s.abn)),
  ]
  const [localSuppliers, setLocalSuppliers] = useState<Supplier[]>([])
  const suppliers: Supplier[] = [...mergedSuppliers, ...localSuppliers]
  const [search, setSearch]       = useState('')
  const [catFilter, setCatFilter] = useState<SupplierCategory|'ALL'>('ALL')
  const [showAdd, setShowAdd]     = useState(false)
  const [selected, setSelected]   = useState<Supplier|null>(null)
  const [preferred, setPreferred] = useState(false)

  // Add supplier form state
  const [form, setForm] = useState({ companyName:'', abn:'', contactName:'', phone:'', email:'', address:'', categories:[] as SupplierCategory[] })
  const [abnStatus, setAbnStatus] = useState<ABNStatus>('IDLE')
  const setF = (k:string,v:any)=>setForm(f=>({...f,[k]:v}))

  const filtered = suppliers.filter(s=>{
    if (catFilter!=='ALL'&&!s.categories.includes(catFilter)) return false
    if (preferred&&!s.isPreferred) return false
    if (search&&!s.companyName.toLowerCase().includes(search.toLowerCase())&&!s.abn.includes(search)) return false
    return true
  })

  const handleSave = () => {
    if (!form.companyName) return
    const newSup: Supplier = {...({} as any),
      id:`sup-${Date.now()}`, companyName:form.companyName, abn:form.abn, contactName:form.contactName, phone:form.phone, email:form.email, address:form.address, website:'', categories:form.categories.length?form.categories:['ELECTRICAL_SIGNALS'], status:'PENDING_APPROVAL' as SupplierStatus, isPreferred:false, rating:3, paymentTermsDays:30, distanceFromDepots:{BNE:0,SC:0}, notes:'', createdAt:new Date().toISOString(),
    }
    setLocalSuppliers(p => [newSup, ...p])
    // Persist to Neon (fire-and-forget)
    createSupplier({
      companyName: form.companyName,
      abn:         form.abn         || undefined,
      contactName: form.contactName || undefined,
      phone:       form.phone       || undefined,
      email:       form.email       || undefined,
      address:     form.address     || undefined,
      category:    form.categories[0] || undefined,
    })
    setShowAdd(false)
    setForm({companyName:'',abn:'',contactName:'',phone:'',email:'',address:'',categories:[]})
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-gray-900">Suppliers</h1><p className="text-sm text-gray-500 mt-0.5">Supplier registry · ABN verification · compliance tracking</p></div>
        {canEdit&&<button onClick={()=>setShowAdd(!showAdd)} className="btn-primary flex items-center gap-2 text-sm"><Plus className="w-4 h-4"/>Add supplier</button>}
      </div>

      {showAdd&&(
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between"><h4 className="font-semibold text-blue-900">New supplier</h4><button onClick={()=>setShowAdd(false)}><X className="w-4 h-4 text-gray-400"/></button></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Company name <span className="text-red-500">*</span></label><input className="input text-sm" value={form.companyName} onChange={e=>setF('companyName',e.target.value)} placeholder="Pty Ltd"/></div>
            <div className="col-span-2"><ABNField abn={form.abn} onChange={v=>setF('abn',v)}/></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Contact name</label><input className="input text-sm" value={form.contactName} onChange={e=>setF('contactName',e.target.value)}/></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Phone</label><input className="input text-sm" value={form.phone} onChange={e=>setF('phone',e.target.value)}/></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Email</label><input className="input text-sm" value={form.email} onChange={e=>setF('email',e.target.value)}/></div>
          </div>
          <div className="flex justify-end gap-2"><button onClick={()=>setShowAdd(false)} className="btn-secondary text-sm">Cancel</button><button onClick={handleSave} disabled={!form.companyName} className="btn-primary text-sm disabled:opacity-50">Save supplier</button></div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/><input className="input pl-9 text-sm" placeholder="Search suppliers or ABN…" value={search} onChange={e=>setSearch(e.target.value)}/></div>
        <select className="input text-sm w-auto" value={catFilter} onChange={e=>setCatFilter(e.target.value as any)}>
          <option value="ALL">All categories</option>
          {(Object.keys(SUPPLIER_CATEGORY_META) as SupplierCategory[]).map((c: SupplierCategory)=><option key={c} value={c}>{SUPPLIER_CATEGORY_META[c].label}</option>)}
        </select>
        <button onClick={()=>setPreferred(!preferred)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${preferred?'bg-amber-100 text-amber-700':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>⭐ Preferred only{preferred?' ✓':''}</button>
      </div>

      <div className="flex gap-4 items-start">
        <div className={`${selected?'w-1/2':'w-full'} space-y-2 transition-all`}>
          {filtered.map(s=>{
            const sc=STATUS_COLORS[s.status]; const isSel=selected?.id===s.id
            const cs=getSupplierComplianceStatus(s.id); const cm=COMPLIANCE_STATUS_META[cs]
            return (
              <div key={s.id} onClick={()=>setSelected(isSel?null:s)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel?'border-blue-400 ring-2 ring-blue-100':cs==='EXPIRED'?'border-red-200':cs==='EXPIRING_SOON'?'border-amber-300':'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.bg} ${sc.text}`}>{s.status}</span>
                      {s.isPreferred&&<span className="text-[10px] text-amber-600 font-medium">⭐ Preferred</span>}
                      {cs!=='VALID'&&<span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${cm.bg} ${cm.text} ${cm.border}`}>{cm.label}</span>}
                    </div>
                    <div className="font-semibold text-gray-900">{s.companyName}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>ABN {s.abn}</span>
                      {s.categories.slice(0,2).map(c=><span key={c} className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{SUPPLIER_CATEGORY_META[c as SupplierCategory]?.label}</span>)}
                      <StarRating rating={s.rating}/>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel?'text-blue-500':'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
          {filtered.length===0&&<div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">No suppliers found</div>}
        </div>

        {selected&&(
          <div className="w-1/2 sticky top-6" style={{maxHeight:'calc(100vh - 160px)'}}>
            <SupplierDetailPanel supplier={selected} onClose={()=>setSelected(null)}/>
          </div>
        )}
      </div>
    </div>
  )
}
